import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { retrieveAccessTokenFromBackend } from './security';

let accessToken: string | null = null;

// Avoid spamming the backend with token requests if retrieval fails.
let tokenFetchFailed = false;

// Track ongoing token refresh to prevent multiple concurrent refresh attempts
let tokenRefreshPromise: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
  // If a refresh is already in progress, wait for it
  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  // Clear the expired token
  accessToken = null;
  tokenFetchFailed = false;

  // Start the refresh
  tokenRefreshPromise = (async () => {
    try {
      const freshToken = await retrieveAccessTokenFromBackend();
      if (freshToken) {
        accessToken = freshToken;
        return freshToken;
      } else {
        tokenFetchFailed = true;
        return null;
      }
    } catch {
      tokenFetchFailed = true;
      return null;
    } finally {
      // Clear the promise so future requests can trigger a new refresh if needed
      tokenRefreshPromise = null;
    }
  })();

  return tokenRefreshPromise;
}

async function getAuthHeader(): Promise<Record<string, string>> {
  let token = accessToken;

  // If we already know token retrieval is failing, don't keep trying.
  if (token == null && !tokenFetchFailed) {
    try {
      const freshToken = await retrieveAccessTokenFromBackend();

      if (freshToken) {
        accessToken = freshToken;
        token = freshToken;
      } else {
        tokenFetchFailed = true;
      }
    } catch {
      tokenFetchFailed = true;
      token = null;
    }
  }

  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
});

apiClient.interceptors.request.use(async (config) => {
  const authHeader = await getAuthHeader();
  const headers = config.headers ?? {};
  Object.assign(headers as Record<string, string>, authHeader);
  config.headers = headers;
  return config;
});

// Response interceptor to handle token expiration
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // If we get a 401 and haven't already retried this request
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const newToken = await refreshToken();

        if (newToken) {
          // Update the stored token so the request interceptor will use it
          // The request interceptor will run again on retry and get the fresh token
          accessToken = newToken;
          // Retry the original request - the request interceptor will add the new token
          return apiClient.request(originalRequest);
        } else {
          // Token refresh failed - user needs to re-authenticate
          // Let the error propagate so the calling code can handle it
          return Promise.reject(error);
        }
      } catch (error) {
        // Token refresh failed - let the error propagate
        return Promise.reject(error);
      }
    }

    // For non-401 errors or already-retried requests, just pass through
    return Promise.reject(error);
  },
);

export { getAuthHeader };
